import { type AxiosResponse } from 'axios'
import { type Course } from '~/types/course'
import { LicenseSchema, type License } from '~/types/license'
import { type RestList } from '~/types/rest'
import { type Software } from '~/types/softwares'
import { client } from './base'
import { type Timezone, TimezoneSchema } from '~/types/timezone'
import { AuthResponseSchema, type AuthResponse } from '~/types/auth'
import { type Participant } from '~/types/participant'

class AdminApi {
  getCourses = async (): Promise<AxiosResponse> =>
    await client.get('/api/admin/courses')

  getUpcomingCourses = async (): Promise<AxiosResponse> =>
    await client.get('/api/admin/upcoming-courses')

  getPastCourses = async (): Promise<AxiosResponse> =>
    await client.get('/api/admin/past-courses')

  getCourse = async (id: number): Promise<AxiosResponse<{ data: Course }>> =>
    await client.get(`/api/admin/courses/${id}`)

  removeCourse = async (id: number): Promise<AxiosResponse> =>
    await client.delete(`/api/admin/courses/${id}`)

  duplicateCourse = async (id: number): Promise<AxiosResponse> =>
    await client.post(`/api/admin/courses/${id}/duplicate`)

  getCourseParticipants = async (id: number): Promise<AxiosResponse> =>
    await client.get(`/api/courses/${id}/participants`)

  getWaitlists = async (params: {
    course_id: number
    search?: string
    per_page?: number
  }): Promise<AxiosResponse> =>
    await client.get('/api/admin/waitlists', { params })

  confirmWaitlist = async (id: number): Promise<AxiosResponse> =>
    await client.post(`/api/admin/waitlists/${id}/confirm`)

  createStudent = async (id: number, payload: any): Promise<AxiosResponse> =>
    await client.post(`/api/admin/courses/${id}/students`, payload)

  editStudent = async (
    id: number,
    studentId: number,
    payload: any
  ): Promise<AxiosResponse> =>
    await client.put(`/api/admin/courses/${id}/students/${studentId}`, payload)

  editStudentEnabled = async (
    id: number,
    studentId: number
  ): Promise<AxiosResponse> =>
    await client.put(`/api/admin/courses/${id}/students/${studentId}/enabled`)

  editStudentPreAccess = async (
    id: number,
    studentId: number
  ): Promise<AxiosResponse> =>
    await client.put(
      `/api/admin/courses/${id}/students/${studentId}/pre-access`
    )

  editStudentPasskeyRequired = async (
    id: number,
    studentId: number
  ): Promise<AxiosResponse> =>
    await client.put(
      `/api/admin/courses/${id}/students/${studentId}/passkey-required`
    )

  getCourseServers = async (id: number): Promise<AxiosResponse> =>
    await client.get(`/api/courses/${id}/servers`)

  createServer = async (payload: any): Promise<AxiosResponse> =>
    await client.post('/api/servers', payload)

  updateServer = async (
    serverId: number,
    payload: any
  ): Promise<AxiosResponse> =>
    await client.put(`/api/servers/${serverId}`, payload)

  removeServer = async (serverId: number): Promise<AxiosResponse> =>
    await client.delete(`/api/servers/${serverId}`)

  syncServers = async (courseId: number): Promise<AxiosResponse> =>
    await client.post(`/api/courses/${courseId}/sync-servers`)

  syncServer = async (serverId: number): Promise<AxiosResponse> =>
    await client.post(`/api/servers/${serverId}/sync`)

  sendAllLoginDetails = async (id: number): Promise<AxiosResponse> =>
    await client.post(`/api/admin/courses/${id}/send-login-info`)

  sendStudentLoginDetails = async (
    id: number,
    studentId: number,
    testing = false
  ): Promise<AxiosResponse> =>
    await client.post(
      `/api/admin/courses/${id}/students/${studentId}/send-login-info`,
      { testing }
    )

  importStudent = async (
    courseId: number,
    file: File
  ): Promise<AxiosResponse> => {
    const form = new FormData()
    form.append('file', file)
    return await client.post(
      `/api/admin/courses/${courseId}/import-students`,
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  }

  createCourse = async (payload: any): Promise<AxiosResponse> =>
    await client.post('/api/admin/courses', payload)

  editCourse = async (id: number, payload: any): Promise<AxiosResponse> =>
    await client.put(`/api/admin/courses/${id}`, payload)

  updateCourseSettings = async (
    id: number,
    payload: Partial<Record<
    'on_demand' | 'is_auto_allocate_server' | 'sync_servers_enabled',
    boolean
    >>
  ): Promise<AxiosResponse> =>
    await client.patch(`/api/admin/courses/${id}/settings`, payload)

  editEmailTemplate = async (
    id: number,
    payload: any
  ): Promise<AxiosResponse> =>
    await client.put(`/api/admin/courses/${id}/email-template`, payload)

  restoreEmailTemplate = async (id: number): Promise<AxiosResponse> =>
    await client.put(`/api/admin/courses/${id}/restore-email-template`)

  getUsers = async (): Promise<AxiosResponse> =>
    await client.get('/api/admin/users')

  createUser = async (payload: any): Promise<AxiosResponse> =>
    await client.post('/api/admin/users', payload)

  getUser = async (id: number): Promise<AxiosResponse> =>
    await client.get(`/api/admin/users/${id}`)

  editUser = async (id: number, payload: any): Promise<AxiosResponse> =>
    await client.put(`/api/admin/users/${id}`, payload)

  getDoctorSupports = async (id: number): Promise<AxiosResponse> =>
    await client.get(`/api/softwares/${id}/users`)

  createSoftware = async (payload: any): Promise<AxiosResponse> =>
    await client.post('/api/admin/softwares', payload)

  editSoftware = async (id: number, payload: any): Promise<AxiosResponse> =>
    await client.put(`/api/admin/softwares/${id}`, payload)

  assignCourseUser = async (
    id: number,
    userId: number
  ): Promise<AxiosResponse> =>
    await client.put(`/api/admin/courses/${id}/assign-doctor`, {
      user_id: userId
    })

  getNativeAppstreamLink = async (
    id: number,
    studentId: number
  ): Promise<AxiosResponse> =>
    await client.get(`/api/admin/courses/${id}/students/${studentId}/appstream`)

  uploadCourseEmailAttachments = async (
    courseId: number,
    file: File
  ): Promise<AxiosResponse> => {
    return await client.postForm(
      `/api/admin/courses/${courseId}/email-attachments`,
      { file }
    )
  }

  getCourseAttachments = async (id: number): Promise<AxiosResponse> =>
    await client.get(`/api/admin/courses/${id}/email-attachments`)

  deleteCourseAttachment = async (
    id: number,
    fileId: number
  ): Promise<AxiosResponse> =>
    await client.delete(`/api/admin/courses/${id}/email-attachments/${fileId}`)

  getAppstreams = async (courseId: number): Promise<AxiosResponse> =>
    await client.get(`/api/courses/${courseId}/appstreams`)

  createAppstream = async (payload: any): Promise<AxiosResponse> =>
    await client.post('/api/appstreams', payload)

  updateAppstream = async (id: number, payload: any): Promise<AxiosResponse> =>
    await client.put(`/api/appstreams/${id}`, payload)

  deleteAppstream = async (id: number): Promise<AxiosResponse> =>
    await client.delete(`/api/appstreams/${id}`)

  getDoctors = async (): Promise<AxiosResponse> =>
    await client.get('/api/admin/doctors')

  getDisabledUsers = async (): Promise<AxiosResponse> =>
    await client.get('/api/disabled-users')

  disableUser = async (id: number): Promise<AxiosResponse> =>
    await client.post(`/api/users/${id}/disable`)

  enableuser = async (id: number): Promise<AxiosResponse> =>
    await client.post(`/api/users/${id}/enable`)
}

export default new AdminApi()

export const apiGetLicenses = async (): Promise<RestList<License>> => {
  const { data } = await client.get('/api/licenses')

  return {
    ...data,
    data: LicenseSchema.array().parse(data.data)
  }
}

export const apiCreateLicense = async (payload: any): Promise<License> => {
  const { data } = await client.post('/api/licenses', payload)
  return LicenseSchema.parse(data.data)
}

export const apiUpdateLicense = async (
  id: number,
  payload: any
): Promise<License> => {
  const { data } = await client.put(`/api/licenses/${id}`, payload)
  return LicenseSchema.parse(data.data)
}

export const apiDeleteLicense = async (id: number): Promise<void> => {
  await client.delete(`/api/licenses/${id}`)
}

export const apiImportLicenses = async (params: any): Promise<void> => {
  const form = new FormData()
  form.append('file', params.file)
  form.append('software_id', String(params.software_id))
  await client.post('/api/import-licenses', form, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

export const apiSyncLicensesInfo = async (): Promise<void> => {
  await client.post('/api/sync-licenses-info')
}

export const apiGetSoftwares = async (): Promise<RestList<Software>> => {
  const { data } = await client.get('/api/admin/softwares')
  return data
}

export const apiGetTimezones = async (): Promise<Timezone[]> => {
  const { data } = await client.get('/api/timezones')
  return TimezoneSchema.array().parse(data.data)
}

export const apiLogin = async (credentials): Promise<AuthResponse> => {
  const { data } = await client.post('/api/login', credentials)
  return AuthResponseSchema.parse(data)
}

export interface ChangePasswordPayload {
  new_password: string
  new_password_confirmation: string
  token: string
}
export const apiChangePassword = async (
  values: ChangePasswordPayload
): Promise<void> => {
  await client.post(`/api/change-password?token=${values.token}`, values)
}

export const apiGetParticipant = async (id: number): Promise<Participant> => {
  const { data } = await client.get(`/api/participants/${id}`)
  return data.data
}

export const apiCancelRegistration = async (id: number): Promise<void> => {
  await client.post(`/api/registrations/${id}/cancel`)
}

export const apiUserAuthByAdmin = async (id: number): Promise<string> => {
  const { data } = await client.post(`/api/admin/users/${id}/auth`)

  return data.token
}

export const apiForgotPassword = async (credentials): Promise<any> => {
  const { data } = await client.post('/api/forgot-password', credentials)
  return data
}

export const apiResetPassword = async (payload): Promise<any> => {
  const { data } = await client.post('/api/reset-password', payload)
  return data
}

export const apiGetEmailTemplate = async (
  type: string,
  courseId
): Promise<any> => {
  const { data } = await client.get('/api/email-templates', {
    params: {
      type,
      course_id: courseId
    }
  })
  return data.data
}

export const apiSaveEmailTemplate = async (
  id: number,
  payload: { title: string, content: string }
): Promise<any> => {
  const res = await client.post(`/api/email-templates/${id}`, {
    ...payload
  })

  return res.data.data
}

export const apiUploadEmailTemplateAttachment = async (
  id: number,
  file: File
): Promise<AxiosResponse> => {
  return await client.postForm(`/api/email-templates/${id}/attachments`, {
    file
  })
}

export const apiDeleteEmailTemplateAttachment = async (
  id: number,
  fileId: number
): Promise<AxiosResponse> => {
  return await client.delete(`/api/email-templates/${id}/attachments/${fileId}`)
}

export const apiRestoreDefaultEmailTemplate = async (
  id: number
): Promise<any> => {
  const res = await client.post(`/api/email-templates/${id}/restore-default`)

  return res.data?.data
}

export const apiSendTestEmail = async (
  id: number,
  email: string
): Promise<any> => {
  await client.post(`/api/email-templates/${id}/send-test-mail`, {
    email
  })
}

export const apiSendEmailTemplate = async (
  id: number,
  template: string
): Promise<any> => {
  await client.post(`/api/courses/${id}/send-mail`, {
    template
  })
}

export async function apiDeleteRegistration(id: number) {
  await client.delete(`/api/registrations/${id}`)
}

export async function apiRefundRegistration(id: number) {
  await client.post(`/api/admin/registrations/${id}/refund`)
}

export const apiResetParticipantPasskey = async (id: number): Promise<any> => {
  return await client.post(`/api/participants/${id}/reset-passkey`)
}

export const apiGetUploadLocations = async (): Promise<any> => (await client.get('/api/admin/upload-locations')).data.data
export const apiCreateUploadLocation = async (payload: any): Promise<any> => (await client.post('/api/admin/upload-locations', payload)).data.data
export const apiUpdateUploadLocation = async (id: number, payload: any): Promise<any> => (await client.put(`/api/admin/upload-locations/${id}`, payload)).data.data
export const apiDeleteUploadLocation = async (id: number): Promise<void> => { await client.delete(`/api/admin/upload-locations/${id}`) }
export const apiTestUploadLocation = async (id: number): Promise<any> => (await client.post(`/api/admin/upload-locations/${id}/test-connection`)).data
